import React from 'react';
import { TrendingUp, TrendingDown, Activity, Zap } from 'lucide-react';
import { UptimeCheck, formatResponseTime } from './types';
import type { Site } from '../types';

interface UptimeStatsCardsProps {
  checks: UptimeCheck[];
  site: Site;
}

function getRunningFor(site: Site): { days: number; hours: number } {
  const ref = site.uptime_last_up_at || site.created_at;
  if (!ref) return { days: 0, hours: 0 };
  const ms = Date.now() - new Date(ref).getTime();
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  return { days, hours };
}



export const UptimeStatsCards: React.FC<UptimeStatsCardsProps> = ({ checks, site }) => {
  const total = checks.length;
  const upCount = checks.filter(c => c.status === 'up').length;
  const downCount = total - upCount;
  const uptimePct = total > 0 ? (upCount / total) * 100 : 100;
  const downtimePct = total > 0 ? (downCount / total) * 100 : 0;
  const runningFor = getRunningFor(site);
  const isDown = site.uptime_status === 'down';

  // Calculate Average and Best Response Times
  const validChecks = checks.filter(c => c.status === 'up' && c.total_time_ms != null);
  const avgResponseTime = validChecks.length > 0
    ? validChecks.reduce((acc, c) => acc + (c.total_time_ms || 0), 0) / validChecks.length
    : 0;

  const bestResponseTime = validChecks.length > 0
    ? Math.min(...validChecks.map(c => c.total_time_ms || Infinity))
    : 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="rounded-xl border border-border bg-card px-5 py-4">
        <p className="text-xs font-medium text-muted-foreground mb-1">Uptime</p>
        <p className="text-3xl font-bold text-foreground tabular-nums">
          {uptimePct.toFixed(2)}%
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card px-5 py-4">
        <p className="text-xs font-medium text-muted-foreground mb-1">Downtime</p>
        <p className={`text-3xl font-bold tabular-nums ${downtimePct > 0 ? 'text-red-500' : 'text-foreground'}`}>
          {downtimePct.toFixed(2)}%
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card px-5 py-4">
        <div className="flex items-center gap-1.5 mb-1">
          {isDown ? (
            <TrendingDown className="w-3.5 h-3.5 text-red-500" />
          ) : (
            <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
          )}
          <p className="text-xs font-medium text-muted-foreground">
            {isDown ? 'Down for' : 'Running for'}
          </p>
        </div>
        <p className="text-3xl font-bold text-foreground tabular-nums">
          {runningFor.days > 0 ? (
            <>
              {runningFor.days}<span className="text-xl ml-0.5 text-muted-foreground">d</span>
              {runningFor.hours > 0 && (
                <> {runningFor.hours}<span className="text-xl ml-0.5 text-muted-foreground">h</span></>
              )}
            </>
          ) : runningFor.hours > 0 ? (
            <>{runningFor.hours}<span className="text-xl ml-0.5 text-muted-foreground">h</span></>
          ) : (
            <>&lt;1<span className="text-xl ml-0.5 text-muted-foreground">h</span></>
          )}
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card px-5 py-4">
        <div className="flex items-center gap-1.5 mb-1">
          <Activity className="w-3.5 h-3.5 text-muted-foreground" />
          <p className="text-xs font-medium text-muted-foreground">Avg. Response Time</p>
        </div>
        <p className="text-3xl font-bold text-foreground tabular-nums">
          {validChecks.length > 0 ? formatResponseTime(avgResponseTime) : 'N/A'}
        </p>
        {validChecks.length > 0 && (
          <div className="flex items-center gap-1.5 mt-1">
            <Zap className="w-3.5 h-3.5 text-emerald-500" />
            <p className="text-sm text-muted-foreground">Best: {formatResponseTime(bestResponseTime)}</p>
          </div>
        )}
      </div>
    </div>
  );
};
